import React, {useState, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useQuery} from 'react-query';
import CustomersWrapper from './CustomersWrapper';
import {SearchInput} from 'components';
import {useSelector} from 'react-redux';
import BaasWalletTable from '../../components/BaasWalletTable';
import {fetchBAASCompanyWallets} from 'requests/queries/baas';
import {Constants} from 'helpers';
import moment from 'moment';

const initialFilters = {
  start: '',
  end: '',
};

function CustomerWallets() {
  const navigate = useNavigate();
  const {slug} = useParams();
  const [searchText, setSearchText] = useState('');
  const {userDetails} = useSelector(state => state?.auth);
  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });
  const [filters, setFilters] = useState(initialFilters);
  const [openFilter, setOpenFilter] = useState(false);

  const formatDate = date => {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DD');
  };

  const params = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      ...query,
      search: searchText,
      customer_id: slug,
      start_date: formatDate(filters?.start),
      end_date: formatDate(filters?.end),
    }),
    [query, searchText, userDetails, slug, filters?.start, filters?.end],
  );

  const summaryParams = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      customer_id: slug,
      start_date: formatDate(filters?.start),
      end_date: formatDate(filters?.end),
    }),
    [userDetails, slug, filters?.start, filters?.end],
  );

  const {data: company_wallets, isLoading} = useQuery(
    ['customer-wallets', {params}],
    () => fetchBAASCompanyWallets({params}),
    {
      retry: false,
      meta: {preventGlobalError: true},
    },
  );

  const wallets = useMemo(
    () => !isLoading && company_wallets && company_wallets?.entity?.wallets,
    [company_wallets, isLoading],
  );

  const handlePageChange = page => {
    setQuery(prev => ({...prev, page}));
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', page);
    const newSearchParams = searchParams.toString();
    navigate(`?${newSearchParams}`);
  };

  const handleLimitChange = length => {
    setQuery(prev => ({...prev, length, page: 1}));
  };

  const handleResetFilter = () => {
    navigate('');
    setFilters(initialFilters);
    setOpenFilter(true);
    setSearchText('');
  };

  const handleViewWallet = wallet => {
    navigate(
      Constants.PAGES_URL.BAAS_ACCOUNT_DETAIL?.replace(
        ':slug',
        wallet?.wallet_id,
      ),
    );
  };

  return (
    <CustomersWrapper>
      <div className="mt-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-grey text-[18px] font-medium">Wallets</h3>

          <div className="flex items-center gap-3 flex-wrap">
            <SearchInput onChange={value => setSearchText(value)} />
            <BaasWalletTable.FilterButton
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </div>
        </div>

        <BaasWalletTable
          wallets={wallets || []}
          isLoading={isLoading}
          customerId={slug}
          onViewWallet={handleViewWallet}
          className="mt-7"
          showEdit={false}
          totalPages={company_wallets?.entity?.total_pages || 1}
          currentPage={Number(company_wallets?.entity?.current_page) || 1}
          onPageChange={handlePageChange}
          limit={query.length}
          onLimitChange={handleLimitChange}
          totalItems={company_wallets?.entity?.current_page || 0}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          filters={filters}
          setFilters={setFilters}
          filterOptions={[]}
          onResetFilter={handleResetFilter}
          navigate={navigate}
          searchText={searchText}
          setSearchText={setSearchText}
          initialFilters={initialFilters}
        />
      </div>
    </CustomersWrapper>
  );
}

export default CustomerWallets;
